import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  ReadLaborCostsInStoresDepartmentBased,
  ReadLaborCostsInStoresRoleBased,
} from "@/schemas/laborCostsInStores";
import { useTranslation } from "react-i18next";
import { getNumberFormatter } from "@/lib/utils/getNumberFormatter";
import { calculateBaseAmount } from "@/modules/projects/components/invoiceSpecifications/utils/calculateBaseAmount";
import { calculateInvoicedAmount } from "@/modules/projects/components/invoiceSpecifications/utils/calculateInvoicedAmount";
import DataTableCellField from "@/components/data-table/components/DataTableCellField";
import DataTableFormulaInputField from "@/components/data-table/components/DataTableFormulaInputField";
import { DirtyAwareCell } from "@/contexts/FormTableCellDirtyContext";

const getBaseAmountFieldPath = (storeId: string, fieldName: string) => {
  return `stores.${storeId}.${fieldName}` as const;
};

const getInvoicedAmountFieldPath = (
  storeId: string,
  fieldName: "base_amount" | "addition" | "deduction",
) => {
  return `stores.${storeId}.${fieldName}` as const;
};

export const useLaborCostsInStoresColumnOverrides = (): Record<
  string,
  ColumnDef<
    ReadLaborCostsInStoresDepartmentBased | ReadLaborCostsInStoresRoleBased,
    unknown
  >
> => {
  const { i18n } = useTranslation();
  const numberFormatter = useMemo(
    () => getNumberFormatter(i18n.language),
    [i18n.language],
  );

  return useMemo(
    () => ({
      planned_hours: {
        id: "planned_hours",
        cell: ({ getValue }) => numberFormatter(getValue() as number),
      },
      hours: {
        id: "hours",
        cell: ({ row }) => {
          const fieldPath = `stores.${row.original.id}.hours`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
      },
      rate: {
        id: "rate",
        cell: ({ row }) => {
          const fieldPath = `stores.${row.original.id}.rate`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
      },
      base_amount: {
        id: "base_amount",
        cell: ({ row }) => {
          const fieldPath = `stores.${row.original.id}.base_amount`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableFormulaInputField
                name={fieldPath}
                formula={({ hours, rate }) =>
                  calculateBaseAmount({
                    hours,
                    rate,
                    fallbackHours: row.original.hours,
                    fallbackRate: row.original.rate,
                  })
                }
                watchFields={[`hours`, `rate`]}
                getFieldPath={(fieldName) =>
                  getBaseAmountFieldPath(row.original.id as string, fieldName)
                }
                type="number"
              />
            </DirtyAwareCell>
          );
        },
      },
      addition: {
        id: "addition",
        cell: ({ row }) => {
          const fieldPath = `stores.${row.original.id}.addition`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
      },
      deduction: {
        id: "deduction",
        cell: ({ row }) => {
          const fieldPath = `stores.${row.original.id}.deduction`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
      },
      invoiced_amount: {
        id: "invoiced_amount",
        cell: ({ row }) => {
          const fieldPath = `stores.${row.original.id}.invoiced_amount`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableFormulaInputField
                name={fieldPath}
                formula={calculateInvoicedAmount}
                watchFields={["base_amount", "addition", "deduction"]}
                getFieldPath={(fieldName) =>
                  getInvoicedAmountFieldPath(
                    row.original.id as string,
                    fieldName,
                  )
                }
                type="number"
              />
            </DirtyAwareCell>
          );
        },
      },
      comment: {
        id: "comment",
        cell: ({ row }) => {
          const fieldPath = `stores.${row.original.id}.comment`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} />
            </DirtyAwareCell>
          );
        },
      },
      include_on_invoice: {
        id: "include_on_invoice",
        cell: ({ row }) => {
          const fieldPath = `stores.${row.original.id}.include_on_invoice`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="checkbox" />
            </DirtyAwareCell>
          );
        },
      },
    }),
    [numberFormatter],
  );
};
